/**
 * Palette de l'égogramme : une teinte par famille (état du moi), déclinée en
 * opacité par sous-famille.
 *
 * Les cinq familles sont des CATÉGORIES — aucune n'est « meilleure » qu'une
 * autre — d'où cinq teintes distinctes plutôt qu'une échelle. À l'intérieur
 * d'une famille en revanche, la variation est une simple déclinaison de la même
 * teinte : les sous-familles appartiennent visiblement à leur famille, on ne
 * leur donne pas une identité concurrente.
 *
 * Les suffixes sont des alphas hexadécimaux : `B3` ≈ 70 %, `80` = 50 %.
 *
 * Ces teintes sont volontairement pastel : leur contraste sur la surface claire
 * est sous 3:1, elles ne peuvent donc JAMAIS porter seules une information. Ici
 * elles ne le font pas — l'axe nomme chaque famille, chaque barre porte son
 * score en clair, chaque carte écrit le nom du groupe. Le texte garde toujours
 * un jeton d'encre, jamais la couleur de la série.
 */

/** Teinte de repli d'une famille absente de la palette (profil inattendu). */
export const COULEUR_FAMILLE_INCONNUE = "#E5E7EB";

type Famille = {
  famille: string;
  /** Vide pour ADULTE, qui n'a pas de sous-famille. */
  sousGroupes: Record<string, string>;
};

/** Clés = noms de groupe produits par `calculerScoresEgogramme`. */
export const COULEURS_EGOGRAMME: Record<string, Famille> = {
  "PARENT CRITIQUE": {
    famille: "#FCA5A5",
    sousGroupes: { PROTECTEUR: "#FCA5A5", PERSECUTEUR: "#FCA5A5B3" },
  },
  "PARENT AIDANT": {
    famille: "#49E3A7",
    sousGroupes: { SUPPORTEUR: "#49E3A7", SAUVEUR: "#49E3A7B3" },
  },
  ADULTE: { famille: "#C4B5FD", sousGroupes: {} },
  "ENFANT LIBRE": {
    famille: "#FDBA74",
    sousGroupes: { INGENU: "#FDBA74", LIBERTAIRE: "#FDBA74B3" },
  },
  "ENFANT ADAPTE": {
    famille: "#818CF8",
    sousGroupes: { MODELE: "#818CF8", SOUMIS: "#818CF8B3", REBELLE: "#818CF880" },
  },
};

/** Teinte d'une famille, par son nom de groupe (ex. "PARENT CRITIQUE"). */
export function couleurFamille(groupe: string): string {
  return COULEURS_EGOGRAMME[groupe]?.famille ?? COULEUR_FAMILLE_INCONNUE;
}

/**
 * Teinte d'une sous-famille (ex. "PERSECUTEUR" dans "PARENT CRITIQUE").
 * Retombe sur la teinte pleine de la famille si la sous-famille est inconnue :
 * mieux vaut une nuance perdue qu'un gris qui suggérerait une autre famille.
 */
export function couleurSousGroupe(groupe: string, sousGroupe: string): string {
  const f = COULEURS_EGOGRAMME[groupe];
  if (!f) return COULEUR_FAMILLE_INCONNUE;
  return f.sousGroupes[sousGroupe] ?? f.famille;
}

/**
 * Teinte associée à une clé de profil ATOMIQUE telle que stockée en base
 * (`ADULTE`, `PARENT_CRITIQUE_PERSECUTEUR`). Sert aux pastilles de la liste des
 * réponses, qui travaillent sur la clé brute et non sur les groupes calculés.
 */
export function couleurProfilAtomique(profil: string): string {
  const segments = profil.trim().split("_");
  if (segments.length === 1) return couleurFamille(segments[0]!);
  if (segments.length === 3) {
    return couleurSousGroupe(`${segments[0]} ${segments[1]}`, segments[2]!);
  }
  return COULEUR_FAMILLE_INCONNUE;
}
